import { LitElement, html, svg, css, nothing } from 'lit';
import { PRINT_STYLES } from './print-styles.js';
import { repeat } from 'lit/directives/repeat.js';
import { StoreMixin } from '../store.js';
import { setFilters } from '../router.js';
import { STATUS_STATES } from '../lib/palette.js';
import {
  HORIZON_SORTS,
  ROW,
  cellText,
  foldLabel,
  horizonColumns,
  horizonExportRows,
  horizonGeometry,
  horizonModel,
  horizonSummary,
  weekBand,
} from '../lib/horizon.js';
import { withFormats } from '../lib/table.js';
import { hours as formatHours, percent, weekLabel, longDate, count } from '../lib/format.js';

/** What a row of each dimension is called, singular and plural. Matches the heatmap's corner cell. */
const ROW_NOUNS = {
  company: ['Company', 'company'],
  department: ['Department', 'departments'],
  service: ['Service', 'services'],
  office: ['Office', 'offices'],
  individual: ['Person', 'people'],
};

const SORT_LABELS = { peak: 'Peak', average: 'Average', label: 'Name' };
const SORTS = HORIZON_SORTS.map((key) => ({ key, label: SORT_LABELS[key] }));

/** Two decimals, as the shared module rounds: the SVG never carries more precision than it has. */
const round = (n) => Math.round((n + Number.EPSILON) * 100) / 100;

/**
 * A horizontal position, as a percentage of the row. The rows carry no `viewBox`: weeks are
 * placed as percentages and everything vertical is in pixels, so a row that stretches to 900
 * pixels stretches its folds and leaves the `↑` marker and the no-capacity label the shape they
 * were drawn in.
 */
const pc = (n) => `${n}%`;

/** At most this many week labels along the axis, whatever the window's length. */
const MAX_WEEK_LABELS = 8;

/**
 * The horizon chart (V-4, PRD 10.3; Phase 41): the capacity heatmap folded, one thin band per
 * row, so forty people fit on a screen without a cell losing its value.
 *
 * The matrix says which week hurts and the small multiples say what shape; this says both at
 * once for many more rows than either can hold. Allocation past the healthy band is folded into
 * 15-point steps: height inside a fold, colour for which fold, so a row stays 24 pixels tall
 * while a 150 per cent week is an unmistakable dark block and a ramp still looks nothing like a
 * spike. The healthy band draws nothing at all — a blank row is a row nobody has to think about.
 *
 * Like the bullet rows and the multiples, it fetches nothing: the page hands it the same
 * `GET /forecast/heatmap` response the matrix is drawn from, so a fold and the cell it stands
 * for are the same figure of the same run. Every number and every fold comes out of
 * `lib/horizon.js`; this component formats, positions and computes nothing else.
 *
 * Encoding (PRD 7.4): the reserved status vocabulary, and nothing but it. The warm arm grows up
 * from the floor — the first fold in the caution colour, the deeper ones in the overloaded
 * colour, a step more intense each time — and the cool arm hangs down from the ceiling in the
 * under-utilized colour, so the two arms are told apart by position as well as by hue. No new
 * token: the fold ramp is mixed from the status colours themselves.
 *
 * Rows are keyed, so changing the sort moves them rather than repainting them.
 */
class HorizonChart extends StoreMixin(LitElement) {
  static properties = {
    data: { attribute: false }, // a GET /forecast/heatmap response, or null
    loading: { type: Boolean },
    error: { type: String },
    week: { type: String }, // the week the bullet rows draw; marked here, set by clicking a cell
    _sort: { state: true },
    _tableOpen: { state: true },
  };

  constructor() {
    super();
    this.data = null;
    this.loading = false;
    this.error = null;
    this.week = null;
    this._sort = 'peak';
    this._tableOpen = false;
  }

  static styles = [
    PRINT_STYLES,
    css`
      :host {
        display: block;
      }
      .card {
        background: var(--surface);
        border: 1px solid var(--border-soft);
        border-radius: var(--radius-card);
        box-shadow: var(--shadow-card);
        padding: 20px 24px 18px;
      }
      .head {
        display: flex;
        flex-wrap: wrap;
        align-items: baseline;
        gap: 6px 18px;
        margin-bottom: 12px;
      }
      .eyebrow {
        font-family: var(--font-eyebrow);
        font-size: 10px;
        font-weight: 700;
        letter-spacing: 0.16em;
        text-transform: uppercase;
        color: var(--ink-muted);
      }
      h2 {
        margin: 2px 0 0;
        font-size: 20px;
        line-height: 1.2;
        color: var(--ink-strong);
      }
      .sub {
        color: var(--ink-muted);
        font-size: 12.5px;
        max-width: 72ch;
      }
      .kpis {
        margin-left: auto;
        display: flex;
        gap: 18px;
        flex-wrap: wrap;
      }
      .kpi {
        text-align: right;
      }
      .kpi .n {
        font-size: 18px;
        font-weight: 700;
        color: var(--ink-strong);
        font-variant-numeric: tabular-nums;
        line-height: 1.1;
      }
      .kpi .l {
        font-size: 11px;
        color: var(--ink-muted);
      }
      .controls {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 8px 16px;
        margin-bottom: 12px;
        font-size: 12.5px;
        color: var(--ink-muted);
      }
      button.link {
        font: inherit;
        font-size: 12.5px;
        border: 0;
        background: none;
        color: var(--link);
        padding: 0;
        font-weight: 600;
        cursor: pointer;
      }
      .segmented {
        display: inline-flex;
        align-items: center;
        gap: 6px;
      }
      .segmented .group {
        display: inline-flex;
        border: 1px solid var(--control-border);
        border-radius: var(--radius-pill);
        background: var(--control-bg);
        padding: 2px;
      }
      .segmented .group button {
        font: inherit;
        border: 0;
        background: none;
        border-radius: var(--radius-pill);
        padding: 3px 10px;
        font-size: 12.5px;
        font-weight: 600;
        color: var(--ink-muted);
        cursor: pointer;
      }
      .segmented .group button[aria-pressed='true'] {
        color: var(--ink-on-brand);
        background: var(--ink-strong);
      }

      /* ---- the rows ---- */
      .rows {
        display: grid;
        gap: 2px;
      }
      .row,
      .axis {
        display: grid;
        grid-template-columns: minmax(96px, 20ch) 1fr minmax(128px, auto);
        align-items: center;
        gap: 10px;
      }
      .row .name {
        display: flex;
        align-items: baseline;
        gap: 6px;
        min-width: 0;
        font-size: 12.5px;
        color: var(--ink-strong);
      }
      .row .name button,
      .row .name .fixed {
        font: inherit;
        font-weight: 600;
        text-align: left;
        color: inherit;
        background: none;
        border: 0;
        padding: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      .row .name button {
        cursor: pointer;
      }
      .row .name button:hover {
        color: var(--link);
        text-decoration: underline;
      }
      .row .name .rank {
        font-family: var(--font-eyebrow);
        font-size: 10px;
        color: var(--ink-faint);
        font-variant-numeric: tabular-nums;
        flex: none;
      }
      .row .figures {
        display: flex;
        align-items: baseline;
        justify-content: flex-end;
        gap: 8px;
        font-size: 11.5px;
        font-variant-numeric: tabular-nums;
        color: var(--ink-muted);
        white-space: nowrap;
      }
      .row .figures .pk {
        font-size: 12.5px;
        font-weight: 700;
        color: var(--ink-strong);
      }
      .row.reference {
        margin-top: 8px;
        padding-top: 8px;
        border-top: 1px solid var(--border);
      }
      .plot {
        display: block;
        width: 100%;
        height: 24px;
        background: var(--plot-bg);
        border: 1px solid var(--border-soft);
        border-radius: 2px;
      }
      .plot:focus-visible {
        outline: 2px solid var(--focus);
        outline-offset: 1px;
      }
      .row[data-selected='true'] .plot {
        outline: 2px solid var(--focus);
        outline-offset: 1px;
      }
      .row[data-selected='true'] .name button {
        color: var(--link);
        text-decoration: underline;
      }
      /* The baseline the folds are measured from: the healthy band, which draws nothing. */
      .baseline {
        stroke: var(--ink-faint);
        stroke-width: 1;
        vector-effect: non-scaling-stroke;
      }
      .weekmark {
        stroke: var(--ink-strong);
        stroke-width: 1;
        stroke-dasharray: 2 2;
        vector-effect: non-scaling-stroke;
      }
      .gap {
        fill: var(--surface-2);
      }
      .gapline {
        stroke: var(--ink-faint);
        stroke-width: 1;
        stroke-dasharray: 2 2;
        vector-effect: non-scaling-stroke;
      }
      /* The marker sits on the deepest fold, so its ink is that band's ink, as a matrix cell's
         is: white on overloaded, dark on the under-utilized step, which white would fail. */
      .beyond {
        font-size: 8px;
        font-weight: 700;
      }
      .beyond[data-arm='warm'] {
        fill: var(--status-overloaded-ink);
      }
      .beyond[data-arm='cool'] {
        fill: var(--status-underload-ink);
      }
      .hit {
        fill: transparent;
        cursor: pointer;
      }
      .empty {
        font-size: 8px;
        fill: var(--ink-muted);
      }

      /* ---- the week axis, stated once for every row ---- */
      .axis {
        margin-bottom: 6px;
      }
      .axis .weeks {
        display: grid;
        font-size: 10.5px;
        color: var(--tick-ink);
        font-variant-numeric: tabular-nums;
      }
      .axis .weeks span {
        grid-row: 1;
        white-space: nowrap;
      }
      .axis .scale {
        font-size: 11px;
        color: var(--ink-muted);
        text-align: right;
      }

      /* ---- chrome ---- */
      .legend {
        display: flex;
        flex-wrap: wrap;
        gap: 6px 16px;
        margin-top: 14px;
        padding: 0;
        list-style: none;
        font-size: 12px;
        color: var(--ink-muted);
      }
      .legend li {
        display: inline-flex;
        align-items: center;
        gap: 6px;
      }
      .legend .sw {
        width: 22px;
        height: 12px;
        border-radius: 2px;
        border: 1px solid var(--border-soft);
        flex: none;
      }
      .legend .sw.blank {
        background: var(--plot-bg);
      }
      .legend .sw.gap {
        background: var(--surface-2);
      }
      .legend .arm {
        font-family: var(--font-eyebrow);
        font-size: 10px;
        letter-spacing: 0.12em;
        text-transform: uppercase;
        color: var(--ink-faint);
      }
      .note {
        margin: 10px 0 0;
        font-size: 12px;
        color: var(--ink-muted);
        max-width: 88ch;
      }
      .state {
        padding: 26px 0;
        color: var(--ink-muted);
        font-size: 13px;
      }
      .state.error {
        color: var(--status-overloaded);
      }
      details.table {
        margin-top: 14px;
      }
      details.table summary {
        cursor: pointer;
        font-size: 12.5px;
        color: var(--ink-muted);
      }
      details.table data-table {
        margin-top: 10px;
      }
      @media (max-width: 720px) {
        .card {
          padding: 16px 14px 14px;
        }
        .row,
        .axis {
          grid-template-columns: minmax(72px, 14ch) 1fr;
        }
        .row .figures {
          grid-column: 1 / -1;
          justify-content: flex-start;
        }
        .axis .scale {
          display: none;
        }
      }
    `,
  ];

  // -------------------------------------------------------------------------------------------
  // Derived

  get _model() {
    return this.data ? horizonModel(this.data, { sort: this._sort }) : null;
  }

  get _nouns() {
    return ROW_NOUNS[this.data?.dimension] ?? ROW_NOUNS.company;
  }

  // -------------------------------------------------------------------------------------------
  // Interaction

  /** Clicking a week sets the week the bullet rows draw, as the matrix's column heads do. */
  _selectWeek(weekStart) {
    this.dispatchEvent(
      new CustomEvent('week-change', {
        detail: { week_start: weekStart === this.week ? null : weekStart },
        bubbles: true,
        composed: true,
      }),
    );
  }

  _toggleEntity(row) {
    const { filters } = this.appState;
    const dimension = this.data?.dimension;
    if (!dimension || dimension === 'company' || row.reference) return;
    const already = filters.dimension === dimension && filters.entity === row.key;
    setFilters({ dimension, entity: already ? null : row.key });
  }

  _isSelected(row) {
    const { filters } = this.appState;
    return (
      !row.reference && filters.dimension === this.data?.dimension && filters.entity === row.key
    );
  }

  _showTooltip(event, row, cell) {
    const tooltip = this.renderRoot.querySelector('chart-tooltip');
    if (!tooltip) return;
    const state = STATUS_STATES.find((s) => s.key === cell.band);
    const exact = (v) => formatHours(v, { exact: true });
    const rows = [
      { label: 'Allocated', value: exact(cell.allocated_hours) },
      { label: '  High confidence', value: exact(cell.high_confidence_hours) },
      { label: 'Capacity', value: exact(cell.capacity_hours) },
      cell.headroom_hours !== null && cell.headroom_hours < 0
        ? { label: 'Over capacity', value: exact(-cell.headroom_hours) }
        : { label: 'Headroom', value: exact(cell.headroom_hours) },
    ];
    const facts = [
      cellText(cell),
      state ? `${state.icon} ${state.label}` : null,
      row.peak_week === cell.week_start ? 'this row’s peak week' : null,
    ];
    tooltip.show({
      x: event.clientX ?? 0,
      y: event.clientY ?? 0,
      title: `${row.label} · week of ${longDate(cell.week_start)}`,
      rows,
      footer: facts.filter(Boolean).join(' · '),
    });
  }

  _hideTooltip() {
    this.renderRoot.querySelector('chart-tooltip')?.hide();
  }

  // -------------------------------------------------------------------------------------------
  // Marks

  /** What a row states in words, for a screen reader: the folds read out as figures. */
  _rowLabel(row) {
    if (!row.has_capacity)
      return `${row.label}: no capacity in any week, ${formatHours(
        row.allocated_hours,
      )} allocated over ${count(row.weeks)} weeks`;
    const over =
      row.weeks_over > 0
        ? `, overloaded in ${count(row.weeks_over)} of ${count(row.weeks)} weeks`
        : ', never overloaded';
    const idle =
      row.weeks_cool > 0 ? `, under the healthy band in ${count(row.weeks_cool)} weeks` : '';
    return `${row.label}: peak ${percent(row.peak_utilization)} in the week of ${weekLabel(
      row.peak_week,
    )}, average ${percent(row.average)}${over}${idle}`;
  }

  _plot(model, row) {
    const g = horizonGeometry(row);
    const selected = this.week ? model.weeks.findIndex((w) => w.week_start === this.week) : -1;
    const mark = selected >= 0 ? weekBand(selected, model.weeks.length) : null;
    return html`
      <svg
        class="plot"
        height=${ROW.height}
        role="img"
        tabindex="0"
        aria-label=${this._rowLabel(row)}
        @pointerleave=${() => this._hideTooltip()}
        @blur=${() => this._hideTooltip()}
      >
        ${g.gaps.map(
          (c) =>
            svg`<rect class="gap" x=${pc(c.x)} y="0" width=${pc(c.width)} height=${ROW.height}></rect>`,
        )}
        ${g.gaps.map(
          (c) => svg`<line
            class="gapline"
            x1=${pc(c.x)}
            x2=${pc(round(c.x + c.width))}
            y1=${ROW.height / 2}
            y2=${ROW.height / 2}
          ></line>`,
        )}
        ${g.cells.flatMap((c) =>
          c.rects.map(
            (r) => svg`<rect
              class="fold"
              data-depth=${r.depth}
              data-band=${r.band}
              x=${pc(r.x)}
              y=${r.y}
              width=${pc(r.width)}
              height=${r.height}
              fill=${r.fill}
            ></rect>`,
          ),
        )}
        ${
          mark
            ? svg`<rect
                class="weekmark"
                fill="none"
                x=${pc(mark.x)}
                y="0.5"
                width=${pc(mark.width)}
                height=${ROW.height - 1}
              ></rect>`
            : nothing
        }
        <line class="baseline" x1="0" x2="100%" y1=${ROW.height} y2=${ROW.height}></line>
        ${g.beyond.map(
          (c) => svg`<text
            class="beyond"
            data-arm=${c.arm}
            x=${pc(round(c.x + c.width / 2))}
            y=${c.arm === 'warm' ? 8 : ROW.height - 2}
            text-anchor="middle"
          >${c.arm === 'warm' ? '↑' : '↓'}</text>`,
        )}
        ${
          row.has_capacity
            ? nothing
            : svg`<text class="empty" x="50%" y=${ROW.height / 2 + 3}
                text-anchor="middle"
              >no capacity</text>`
        }
        ${g.cells.map(
          (c) => svg`<rect
            class="hit"
            data-week=${c.week_start}
            x=${pc(c.x)}
            y="0"
            width=${pc(c.width)}
            height=${ROW.height}
            @pointermove=${(e) => this._showTooltip(e, row, c)}
            @click=${() => this._selectWeek(c.week_start)}
          ></rect>`,
        )}
      </svg>
    `;
  }

  _row(model, row, rank) {
    const dimension = this.data.dimension;
    return html`
      <div
        class=${`row ${row.reference ? 'reference' : ''}`}
        data-key=${row.key}
        data-selected=${this._isSelected(row) ? 'true' : 'false'}
      >
        <div class="name">
          ${rank === null ? nothing : html`<span class="rank">${rank}</span>`}
          ${
            dimension === 'company' || row.reference
              ? html`<span class="fixed" title=${row.label}>${row.label}</span>`
              : html`<button
                  type="button"
                  title=${
                    this._isSelected(row)
                      ? 'Clear the entity filter'
                      : `Filter the views to ${row.label}`
                  }
                  @click=${() => this._toggleEntity(row)}
                >
                  ${row.label}
                </button>`
          }
        </div>
        ${this._plot(model, row)}
        <div class="figures">
          <span class="pk"
            >${row.peak_utilization === null ? '—' : percent(row.peak_utilization)}</span
          >
          <span>peak${row.peak_week ? html` · ${weekLabel(row.peak_week)}` : nothing}</span>
          <span>avg ${row.average === null ? '—' : percent(row.average)}</span>
        </div>
      </div>
    `;
  }

  /** The weeks, stated once above the rows: every row is the same columns, so one axis serves. */
  _axis(model) {
    const n = model.weeks.length;
    const stride = Math.max(1, Math.ceil(n / MAX_WEEK_LABELS));
    return html`
      <div class="axis">
        <div></div>
        <div class="weeks" style=${`grid-template-columns: repeat(${n}, 1fr);`}>
          ${model.weeks.map((w, i) =>
            i % stride === 0
              ? html`<span style=${`grid-column: ${i + 1};`}>${weekLabel(w.week_start)}</span>`
              : nothing,
          )}
        </div>
        <div class="scale">
          ${count(n)} weeks · folds of ${Math.round(model.folds.step * 100)} points
        </div>
      </div>
    `;
  }

  _chart(model) {
    return html`
      ${this._axis(model)}
      <div class="rows">
        ${repeat(
          model.rows,
          (r) => r.key,
          (r, i) => this._row(model, r, this._sort === 'label' ? null : i + 1),
        )}
      </div>
      ${
        model.reference
          ? html`<div class="rows">${this._row(model, model.reference, null)}</div>`
          : nothing
      }
    `;
  }

  _controls() {
    return html`
      <div class="controls">
        <div class="segmented" role="group" aria-label="Row order">
          <span>Sort</span>
          <span class="group">
            ${SORTS.map(
              (s) =>
                html`<button
                  type="button"
                  aria-pressed=${this._sort === s.key ? 'true' : 'false'}
                  @click=${() => (this._sort = s.key)}
                >
                  ${s.label}
                </button>`,
            )}
          </span>
        </div>
        ${
          this.week
            ? html`<button class="link" type="button" @click=${() => this._selectWeek(this.week)}>
                Clear the marked week (${weekLabel(this.week)})
              </button>`
            : nothing
        }
      </div>
    `;
  }

  /** The folds themselves are the legend: every swatch is painted the way a fold is painted. */
  _legend(model) {
    const { warm, cool } = model.folds;
    const swatch = (fold) =>
      html`<li>
        <span class="sw" style=${`background: ${fold.fill};`} aria-hidden="true"></span>
        <span>${foldLabel(fold)}</span>
      </li>`;
    return html`
      <ul class="legend" aria-label="What the folds mean">
        <li><span class="arm">Over</span></li>
        ${warm.map(swatch)}
        <li>
          <span class="sw blank" aria-hidden="true"></span>
          <span
            >Healthy (${percent(model.thresholds.underload_below)} to
            ${percent(model.thresholds.caution_above)}) — blank</span
          >
        </li>
        <li><span class="arm">Under</span></li>
        ${cool.map(swatch)}
        <li>
          <span class="sw gap" aria-hidden="true"></span>
          <span>No capacity — no ratio to fold</span>
        </li>
        <li>
          <span aria-hidden="true">↑ ↓</span>
          <span>Past the deepest fold: the fold saturates, the figure is beside the row</span>
        </li>
      </ul>
    `;
  }

  _subtitle(model) {
    if (!model) return '';
    const rowsOf =
      this.data.dimension === 'company'
        ? 'the company'
        : `${count(model.rows.length)} ${this._nouns[1]}`;
    return `${rowsOf} over ${count(model.weeks.length)} weeks · folded in ${Math.round(
      model.folds.step * 100,
    )}-point steps from the healthy band · sorted by ${SORT_LABELS[model.sort].toLowerCase()} · pipeline ${
      this.data.pipeline_mode
    }`;
  }

  _kpis(model) {
    const s = horizonSummary(model);
    return html`
      <div class="kpis">
        <div class="kpi">
          <div class="n">${count(s.rows_over)}</div>
          <div class="l">of ${count(s.rows)} ${this._nouns[1]} overloaded in a week</div>
        </div>
        <div class="kpi">
          <div class="n">${s.worst ? percent(s.worst.peak.utilization) : '—'}</div>
          <div class="l">
            ${s.worst ? `${s.worst.row.label}, ${weekLabel(s.worst.peak.week_start)}` : 'no peak'}
          </div>
        </div>
      </div>
    `;
  }

  _notes(model) {
    const { filters } = this.appState;
    const s = horizonSummary(model);
    const deepest = model.folds.warm.at(-1);
    const notes = [];
    notes.push(
      html`<p class="note" data-note="folds">
        A week inside the healthy band (${percent(model.thresholds.underload_below)} to
        ${percent(model.thresholds.caution_above)}) draws nothing, so a blank row is a row nobody
        has to think about. Above it the folds grow up from the floor in
        ${Math.round(model.folds.step * 100)}-point steps and darken with each one; below it they
        hang from the ceiling in the under-utilized colour. Height is the allocation inside a fold,
        colour is which fold — that is how a row stays ${ROW.height} pixels tall whatever the week
        does.
      </p>`,
    );
    // What folding costs: past the deepest fold every week looks alike. Said plainly, with the
    // figure that still carries the magnitude, rather than left for the reader to discover.
    if (s.cells_beyond > 0)
      notes.push(
        html`<p class="note" data-note="beyond">
          ${count(s.cells_beyond)} ${s.cells_beyond === 1 ? 'week is' : 'weeks are'} past the
          deepest fold (${foldLabel(deepest)}): the fold saturates and is marked ↑, so a week at
          200% and a week at 800% look the same here. The peak beside the row and the matrix carry
          the magnitude — this view is for finding the shape, not for reading the worst number off
          the screen.
        </p>`,
      );
    if (s.no_capacity > 0)
      notes.push(
        html`<p class="note" data-note="no-capacity">
          ${count(s.no_capacity)} ${s.no_capacity === 1 ? 'row has' : 'rows have'} no capacity in
          any week: role-only demand with nobody staffed to it. There is no ratio to fold, so those
          weeks are grey and the rows sort last; the matrix and the bullet rows state their hours.
        </p>`,
      );
    if (this.data.withheld_count > 0)
      notes.push(
        html`<p class="note" data-note="withheld">
          ${count(this.data.withheld_count)}
          ${this.data.withheld_count === 1 ? 'person is' : 'people are'} not shown: your access
          covers yourself and your reports at the individual grain (the company row still counts
          them).
        </p>`,
      );
    if (filters.entity && filters.dimension === this.data.dimension) {
      const row = model.rows.find((r) => r.key === filters.entity);
      notes.push(
        html`<p class="note" data-note="entity">
          ${
            row ? `${row.label} is highlighted;` : 'The entity in the filter bar is not a row here;'
          }
          every row stays in the chart, because a horizon of one row is a sparkline. Click the name
          again to clear the filter.
        </p>`,
      );
    }
    if (filters.grain === 'day')
      notes.push(
        html`<p class="note" data-note="grain">
          The grain is daily, so the window is the two weeks the matrix draws as weekdays: two
          folded weeks is a comparison, not a shape. Switch the grain to Week for the whole window.
        </p>`,
      );
    if (this.data.dimension !== 'individual')
      notes.push(
        html`<p class="note" data-note="dimension">
          V-4 is built for the crowded dimension: set the dimension to
          <strong>Individual</strong> and every person in scope is one thin row on one fold scale.
        </p>`,
      );
    notes.push(
      html`<p class="note" data-note="week">
        Click a week in any row to draw the bullet rows beneath for it; the marked week is outlined
        in every row.
      </p>`,
    );
    return notes;
  }

  // -------------------------------------------------------------------------------------------
  // Table

  get _columns() {
    // The specification is shared with the server's CSV (lib/horizon.js).
    return withFormats(horizonColumns({ dimension: this.data?.dimension }), {
      byUnit: {
        hours: (v) => (v === null || v === undefined ? '—' : formatHours(v, { exact: true })),
        percent: (v) => percent(v),
      },
      byKey: {
        week_start: (v) => weekLabel(v),
        peak_week: (v) => weekLabel(v),
        band: (v) => (v ? html`<status-badge state=${v} compact></status-badge>` : '—'),
        arm: (v) => (v === 'warm' ? 'Over' : v === 'cool' ? 'Under' : '—'),
        beyond: (v) => (v ? '↑' : ''),
      },
    });
  }

  // -------------------------------------------------------------------------------------------
  // Render

  render() {
    const { run } = this.appState;
    const model = this._model;

    let body;
    if (!run) body = html`<div class="state">No forecast run yet — nothing to draw.</div>`;
    else if (this.error) body = html`<div class="state error">${this.error}</div>`;
    else if (!this.data && this.loading) body = html`<div class="state">Loading the window…</div>`;
    else if (!model) body = html`<div class="state">No weeks in this window.</div>`;
    else
      body = html`
        ${this._controls()} ${this._chart(model)} ${this._legend(model)} ${this._notes(model)}
        <details
          class="table"
          ?open=${this._tableOpen}
          @toggle=${(e) => (this._tableOpen = e.target.open)}
        >
          <summary>Table view</summary>
          <data-table
            caption=${`Allocation folded per ${this._nouns[0].toLowerCase()} and week — ${this._subtitle(
              model,
            )}`}
            export-name="horizon-chart"
            page-size="25"
            dense
            .columns=${this._columns}
            .rows=${horizonExportRows(model)}
          ></data-table>
        </details>
      `;

    return html`
      <div class="card" aria-busy=${this.loading ? 'true' : 'false'}>
        <div class="head">
          <div>
            <div class="eyebrow">Horizon</div>
            <h2>Where are the spikes, across every row at once?</h2>
            <div class="sub">${this._subtitle(model)}</div>
          </div>
          ${model ? this._kpis(model) : nothing}
        </div>
        ${body}
        <chart-tooltip></chart-tooltip>
      </div>
    `;
  }
}

customElements.define('horizon-chart', HorizonChart);
